import { Alert, Card, DataTable, type ColumnDef } from "@/deps/ui-library";
import { useAgeingReport } from "@/api/queries";
import type { AgeingReport } from "@/api/types";
import { formatDate, today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { DateField, useDateParam } from "./reportParts";
import { ReportFrame } from "./ReportFrame";

type Row = AgeingReport["rows"][number];

/** "Current", "1–30", "31–60", "61–90", "Over 90", from the band's upper bounds. */
export function bandLabels(days: number[]): string[] {
  return [
    ...days.map((day, index) => {
      if (day === 0) return "Current";
      return `${index === 0 ? 0 : days[index - 1]! + 1}–${day}`;
    }),
    `Over ${days.at(-1) ?? 0}`,
  ];
}

/** The totals are a row of the table, so each figure sits under its own band. */
const TOTAL = "total";

const COPY = {
  receivable: {
    id: "ar",
    title: "Receivables ageing",
    party: "Customer",
    route: "/reports/receivables-ageing",
  },
  payable: {
    id: "ap",
    title: "Payables ageing",
    party: "Supplier",
    route: "/reports/payables-ageing",
  },
} as const;

/** What each party owes or is owed, from the control account, so its total agrees with the ledger. */
export function AgeingPage({ kind }: { kind: "receivable" | "payable" }) {
  const copy = COPY[kind];
  const [asAt, setAsAt] = useDateParam(copy.route, "asAt", today());
  const { data, isPending, error } = useAgeingReport(kind, asAt);
  const bands = bandLabels(data?.bucketDays ?? [0, 30, 60, 90]);
  const period = `As on ${formatDate(asAt)}`;

  const columns: Array<ColumnDef<Row>> = [
    {
      id: "party",
      header: copy.party,
      cell: ({ row: { original: row } }) =>
        row.partyId === TOTAL ? (
          <span className="font-semibold">Total</span>
        ) : (
          (row.partyName ?? "A party no longer on file")
        ),
    },
    ...bands.map((band, index): ColumnDef<Row> => ({
      id: `b${index}`,
      header: band,
      meta: { textAlign: "right", numeric: true },
      cell: ({ row: { original: row } }) => {
        const value = row.buckets[index] ?? "0.00";
        if (Number(value) === 0) return "";
        return row.partyId === TOTAL ? (
          <span className="font-semibold">{formatAmount(value)}</span>
        ) : (
          formatAmount(value)
        );
      },
    })),
    {
      id: "total",
      header: "Total",
      meta: { textAlign: "right", numeric: true, cellClassName: "font-medium" },
      cell: ({ row: { original: row } }) =>
        row.partyId === TOTAL ? (
          <span className="font-semibold">{formatAmount(row.total)}</span>
        ) : (
          formatAmount(row.total)
        ),
    },
  ];

  return (
    <ReportFrame
      id={copy.id}
      title={copy.title}
      period={period}
      exportable={
        data
          ? () => ({
              title: copy.title,
              period,
              columns: [
                { header: copy.party },
                ...bands.map((header) => ({ header, numeric: true })),
                { header: "Total", numeric: true },
              ],
              rows: data.rows.map((row) => [row.partyName, ...row.buckets, row.total]),
              totals: ["Total", ...data.bucketTotals, data.total],
            })
          : undefined
      }
    >
      <div className="flex flex-wrap items-end gap-2">
        <DateField label="As on" value={asAt} onChange={setAsAt} />
      </div>

      {error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={error instanceof Error ? error.message : "That report could not be loaded."}
        />
      ) : null}

      <Card size="none" className="overflow-hidden">
        <DataTable
          aria-label={copy.title}
          size="sm"
          enableSorting={false}
          columns={columns}
          data={
            data && data.rows.length > 0
              ? [
                  ...data.rows,
                  {
                    partyId: TOTAL,
                    partyName: "Total",
                    buckets: data.bucketTotals,
                    total: data.total,
                  },
                ]
              : []
          }
          getRowId={(row) => row.partyId}
          loading={isPending}
          emptyContent="Nothing is outstanding at this date."
          pagination={{ mode: "manual", show: false }}
          containerClassName="rounded-none border-0"
        />
      </Card>
      <p className="text-fg-muted text-xs">
        Aged from each document's due date, or from its posting date if it has none. Money received
        settles the oldest first.
      </p>
    </ReportFrame>
  );
}

export const ReceivablesAgeingPage = () => <AgeingPage kind="receivable" />;
export const PayablesAgeingPage = () => <AgeingPage kind="payable" />;
